import React, { useState, useEffect, useRef } from "react";

/* FROZEN — end of Phase: Shared-Surface Discovery (Feb 2026).
   Study 1 (additive contribution) is preserved as a stable
   artifact of this phase. Do not modify. If future studies
   contradict its behavior, create a new route rather than
   editing this one. */

/**
 * Vessel — Physics Study (single moment).
 *
 * Tests whether shared thinking can become visible between SONMI
 * and the steward without turning into a canvas, chat, flowchart,
 * dashboard, or collection of glass cards.
 *
 * The moment: Damon and SONMI clarify an unspoken team norm about
 * Simone. SONMI opens with one observation; the steward adds two
 * facets; the three coalesce into a named understanding — "The way
 * Simone tends" — and the Vessel returns to the environment. A
 * single small drop remains on Simone; one subtle ripple emanates
 * and quiets.
 *
 * Nine steps, click-driven with delayed auto-advances for SONMI's
 * gathering and for the dissolution. Correction path
 * ("not quite…") deliberately not tested — see
 * SONMI_SHARED_SURFACE_PHYSICS.md.
 */

export default function Vessel() {
  const [step, setStep] = useState(0);
  const [rippled, setRippled] = useState(false);
  const timerRef = useRef(null);

  // Auto-advances between click steps.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (step === 1) {
      // SONMI's phrase gathers, then settles into presence.
      timerRef.current = setTimeout(() => setStep(2), 1600);
    } else if (step === 3) {
      // Steward's first contribution completes.
      timerRef.current = setTimeout(() => setStep(4), 1300);
    } else if (step === 5) {
      // Steward's second contribution completes.
      timerRef.current = setTimeout(() => setStep(6), 1300);
    } else if (step === 7) {
      // Dissolution begins; ripple triggers slightly into it.
      timerRef.current = setTimeout(() => setRippled(true), 250);
      timerRef.current = setTimeout(() => setStep(8), 1400);
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [step]);

  const reset = () => {
    setRippled(false);
    setStep(0);
  };

  // Phase helper — returns "gathering" | "present" | "settling" | null
  const phaseFor = (appearsAt, gatheringAt) => {
    if (step < appearsAt) return null;
    if (step === gatheringAt) return "gathering";
    if (step >= 6) return "settling";
    return "present";
  };

  const inVessel = step >= 1 && step < 7;

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif" }}
      data-testid="physics-study-root"
    >
      {/* Study label — minimal, at edge. No toolbar, no strip. */}
      <div
        className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400"
        data-testid="study-label"
      >
        SONMI · physics study · placeholder content
      </div>

      {/* Simone — the subject present in the environment. */}
      <div
        className="absolute"
        style={{ left: "16%", top: "50%", transform: "translateY(-50%)" }}
        data-testid="subject-simone"
      >
        <div className="flex flex-col items-center relative">
          <div
            className="rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500 relative"
            style={{
              width: 66,
              height: 66,
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              fontSize: 24,
            }}
          >
            SC
            {/* The drop that remains — only after dissolution has occurred. */}
            <span
              className="absolute rounded-full transition-opacity duration-1200"
              style={{
                right: -3,
                top: -3,
                width: 6,
                height: 6,
                backgroundColor: "#d97706",
                opacity: step >= 8 ? 0.85 : 0,
                boxShadow: step >= 8 ? "0 0 0 2px rgba(217,119,6,0.08)" : "none",
              }}
              data-testid="remaining-drop"
            />
          </div>
          <div
            className="mt-3 text-[14px] text-stone-700"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
          >
            Simone
          </div>

          {/* Ripple — a single expansion outward from Simone at dissolution. */}
          {rippled && (
            <span
              key={rippled ? "r" : "nr"}
              className="absolute pointer-events-none rounded-full"
              style={{
                left: "50%",
                top: 33, // vertical center of the avatar
                transform: "translate(-50%, -50%)",
                width: 66,
                height: 66,
                border: "1px solid rgba(217, 119, 6, 0.4)",
                animation: "sonmi-ripple 1400ms cubic-bezier(0.16, 0.68, 0.44, 1) forwards",
              }}
              data-testid="ripple"
            />
          )}
        </div>
      </div>

      {/* Vessel region — a warmed area of the surface, no border, no card.
          Fades in with the first contribution, fades out at dissolution. */}
      <div
        className="absolute pointer-events-none transition-all duration-1200"
        style={{
          left: "32%",
          top: "16%",
          right: "8%",
          bottom: "18%",
          background:
            "radial-gradient(ellipse at 30% 45%, rgba(251, 191, 36, 0.09) 0%, rgba(251, 191, 36, 0.04) 45%, transparent 78%)",
          opacity: inVessel ? 1 : 0,
          transform: step >= 6 ? "scale(0.94)" : "scale(1)",
          transformOrigin: "30% 55%",
        }}
        data-testid="vessel-region"
      />

      {/* Phrases inside the Vessel. */}
      <div
        className="absolute"
        style={{ left: "34%", top: "20%", right: "10%", bottom: "20%" }}
      >
        {inVessel && (
          <Phrase
            testId="sonmi-phrase"
            attribution="something SONMI has been noticing"
            phase={phaseFor(1, 1)}
            author="sonmi"
            position={{ top: "0%" }}
          >
            She pauses before every schedule change. It seems to be how she takes care of the people she works with.
          </Phrase>
        )}
        {inVessel && (
          <Phrase
            testId="steward-phrase-1"
            attribution="you added"
            phase={phaseFor(3, 3)}
            author="steward"
            position={{ top: "24%" }}
          >
            And she's the one others check with before requesting time off.
          </Phrase>
        )}
        {inVessel && (
          <Phrase
            testId="steward-phrase-2"
            attribution="you added"
            phase={phaseFor(5, 5)}
            author="steward"
            position={{ top: "44%" }}
          >
            She asks questions I would have missed.
          </Phrase>
        )}

        {/* The earned understanding — the naming both parties arrived at. */}
        {step === 6 && (
          <div
            className="absolute transition-all duration-1000"
            style={{
              top: "68%",
              left: 0,
              right: "10%",
              opacity: 1,
              transform: "translateY(0)",
            }}
            data-testid="earned-understanding"
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-2">
              what we've named, together
            </div>
            <div
              className="italic text-stone-900"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 400,
                fontSize: "1.65rem",
                letterSpacing: "-0.005em",
              }}
            >
              The way Simone tends.
            </div>
          </div>
        )}
      </div>

      {/* Contextual affordances — one at a time, at the base of the surface.
          These are not a toolbar; they are the next contribution the moment
          invites. */}
      <div
        className="absolute left-0 right-0 flex justify-center gap-6 text-[12.5px] text-stone-400 transition-opacity duration-500"
        style={{ bottom: "8%" }}
        data-testid="affordances"
      >
        {step === 0 && (
          <button
            onClick={() => setStep(1)}
            data-testid="attend-simone"
            className="hover:text-stone-700 transition-colors"
          >
            attend to simone
          </button>
        )}
        {step === 2 && (
          <button
            onClick={() => setStep(3)}
            data-testid="yes-and"
            className="hover:text-stone-700 transition-colors"
          >
            yes, and…
          </button>
        )}
        {step === 4 && (
          <button
            onClick={() => setStep(5)}
            data-testid="one-more"
            className="hover:text-stone-700 transition-colors"
          >
            there's one more thing
          </button>
        )}
        {step === 6 && (
          <button
            onClick={() => setStep(7)}
            data-testid="yes-settle"
            className="hover:text-stone-700 transition-colors"
          >
            yes
          </button>
        )}
        {step === 8 && (
          <button
            onClick={reset}
            data-testid="reset"
            className="hover:text-stone-700 transition-colors italic"
          >
            watch again
          </button>
        )}
      </div>

      {/* Ripple keyframes — the only motion authored outside of transitions. */}
      <style>{`
        @keyframes sonmi-ripple {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.85; }
          65%  { opacity: 0.35; }
          100% { transform: translate(-50%, -50%) scale(3.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * A phrase inside the Vessel. Not a card, not a bubble.
 * Attribution is a small eyebrow label above the phrase — visible
 * provenance without a chat timeline.
 */
function Phrase({ children, attribution, phase, author, position, testId }) {
  if (!phase) return null;

  const opacity = phase === "gathering" ? 0.35 : phase === "settling" ? 0.55 : 1;
  const yOffset = phase === "gathering" ? 10 : phase === "settling" ? -6 : 0;

  return (
    <div
      className="absolute transition-all duration-1000"
      style={{
        ...position,
        left: 0,
        right: "10%",
        opacity,
        transform: `translateY(${yOffset}px)`,
      }}
      data-testid={testId}
    >
      <div
        className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-2"
        data-testid={`${testId}-attribution`}
      >
        {attribution}
      </div>
      <div
        className={author === "sonmi" ? "italic text-stone-800" : "text-stone-800"}
        style={{
          fontFamily:
            author === "sonmi" ? "'Cormorant Garamond', serif" : "'Outfit', sans-serif",
          fontWeight: 300,
          fontSize: author === "sonmi" ? "1.25rem" : "1.05rem",
          lineHeight: 1.55,
          maxWidth: "560px",
        }}
      >
        {children}
      </div>
    </div>
  );
}
